/**
 * Single source of truth for every `kind` discriminator value in the v2 IPC
 * union. Used by every event variant's Zod schema, by the discriminated union,
 * by exhaustiveness checks on both sides (agent + host), and by the SDK-drift
 * test fixtures.
 *
 * Snake_case to match historical IPC conventions.
 */
export const AGENT_IPC_KIND = {
  // Block lifecycle — symmetric start/delta/stop across every block kind.
  TextBlockStart: "text_block_start",
  TextBlockDelta: "text_block_delta",
  TextBlockStop: "text_block_stop",
  ThinkingBlockStart: "thinking_block_start",
  ThinkingBlockDelta: "thinking_block_delta",
  ThinkingBlockStop: "thinking_block_stop",
  ToolUseBlockStart: "tool_use_block_start",
  ToolUseBlockDelta: "tool_use_block_delta",
  ToolUseBlockStop: "tool_use_block_stop",
  // Unmodeled provider content blocks — one terminal IPC line at stop, no
  // start/delta trio. See UnknownBlockSchema for the raw-block contract.
  UnknownBlock: "unknown_block",
  // The model reasoned and the provider withheld it. A block, not a signal: it
  // takes its slot among the turn's other content. Typed apart from thinking
  // deliberately — folding it as one drops it from the next request and breaks
  // the multi-turn protocol. See RedactedThinkingBlockSchema.
  RedactedThinkingBlock: "redacted_thinking_block",
  // Top-level messages.
  AssistantToolCall: "assistant_tool_call",
  UserToolResult: "user_tool_result",
  // A transcript entry the SDK replayed on resume, not a turn that just
  // happened. Distinct from UserToolResult so a reader cannot mistake replayed
  // work for live work; see UserMessageReplaySchema.
  UserMessageReplay: "user_message_replay",
  TurnUsage: "turn_usage",
  Result: "result",
  Error: "error",
  SystemInit: "system_init",
  // Observability — one variant per channel (split from v1's god-object).
  ObservabilityAssistantError: "observability_assistant_error",
  ObservabilityApiRetry: "observability_api_retry",
  ObservabilityMirrorError: "observability_mirror_error",
  // Sandbox-side recovery signal — emitted once when the runner attempts to
  // close a dangling AskUserQuestion tool_use in the SDK's session jsonl
  // before calling query({ resume }). Carries the outcome so the host can
  // record on the trace whether native resume held or fell back.
  NativeResumeStatus: "native_resume_status",
  // Sandbox-side diagnostics channel — emitted once per turn, getter-read on the
  // host and recorded on the trace only (never a ChatStreamEvent). The dedicated
  // home for any telemetry only the sandbox can measure (cold-start boot deltas
  // today; memory / fs / etc. slot in beside them). Mirrors NativeResumeStatus.
  RunnerMetrics: "runner_metrics",
  // Long-lived-session terminal signal — emitted once, right
  // before the runner exits, when the idle watchdog fires with no
  // background agent still running. Host-orchestration concern only,
  // like NativeResumeStatus/RunnerMetrics.
  SessionIdle: "session_idle",
  // The SDK's cumulative session cost total, stated ahead of an ending the
  // runner may not be able to announce — a bounded interval, or an ask the
  // host sends before its kill timer fires. Host-orchestration concern only,
  // like NativeResumeStatus/RunnerMetrics/SessionIdle. See
  // SessionCostSampleSchema.
  SessionCostSample: "session_cost_sample",
  // Background-task lifecycle — one snapshot of a single task's current state,
  // emitted whenever any SDK task signal changes what the runner knows about
  // it. State rather than edges; see TaskSnapshotSchema.
  TaskSnapshot: "background_task_state",
  // Background-task membership — the complete live set, emitted on every
  // change. The authority on who is running; the per-task snapshots above
  // only enrich what this event admits. See BackgroundTasksLevelSchema.
  BackgroundTasksLevel: "background_tasks_level",
  // SDK-level turn-over signal, forwarded verbatim where the runner
  // environment arms its emission. It marks the turn over, never the work
  // done — see SessionStateChangedSchema.
  SessionStateChanged: "session_state_changed",
  // A SendMessage delivery from a peer background agent, identified by the SDK's own
  // `origin.kind === 'peer'` stamp. See PeerMessageSchema for the
  // provenance contract this relies on.
  PeerMessage: "peer_message",
  // The shared carrier for a signal nobody has designed a rendering for. One
  // kind for all of them: a fold arm names the signal and hands over its
  // payload, and the reader gets a row saying it happened. See
  // GenericSignalSchema for what a fold arm owes.
  GenericSignal: "generic_signal",
  // The model's context was rewritten mid-run and the turns before it are no
  // longer what the model sees. Carries what the compaction cost, so a reader
  // can tell a compacted answer from a wrong one — see ContextCompactedSchema.
  ContextCompacted: "context_compacted",
  // The SDK opened a fresh conversation mid-process, rotating the session id
  // to a value only the next `system/init` reports. See
  // ConversationResetSchema for why the id it carries is the stale one.
  ConversationReset: "conversation_reset",
  // The model refused a request, and the frames it had already produced for it
  // are withdrawn. Carries the withdrawal as block ids the reader can act on,
  // and the refusal's own prose where the SDK sent any — see
  // ModelRefusalSchema.
  ModelRefusal: "model_refusal",
  // What the session is doing right now — waiting on a model call, compacting
  // its context, or neither. One frame per change, because a phase reported
  // once says a session started something and never that it stopped. See
  // SessionPhaseSchema.
  SessionPhase: "session_phase",
  // A foreground tool call is still running — the SDK's 30-second heartbeat,
  // and the Agent tool's subagent-retry frames. The only liveness signal a
  // foreground call has, since membership reports backgrounded work alone. See
  // ToolProgressSchema for why the id it carries is resolved rather than
  // forwarded.
  ToolProgress: "tool_progress",
  // How much reasoning the model has produced so far, as the SDK's own running
  // estimate. The only evidence a thinking block is advancing when its text
  // comes back empty and only the signature arrives. Coalesced at the runner
  // because the SDK emits it per reasoning burst — hundreds per run. See
  // ThinkingTokensSchema for the window and for why the value is an estimate
  // rather than a cost.
  ThinkingTokens: "thinking_tokens",
  // A text notification the loop raised about itself — the SDK's REPL
  // notification queue, which reaches a headless session too. See
  // LoopNotificationSchema.
  LoopNotification: "loop_notification",
  // A plain-text banner from the loop — status lines, slash-command output, and
  // the channel a hook's `systemMessage` arrives on. A separate kind from
  // LoopNotification because they are separate SDK messages with separate
  // fields; see LoopBannerSchema.
  LoopBanner: "loop_banner",
  // The memory-recall supervisor surfaced stored memories into the turn, so the
  // model answered with material that appears nowhere else in the transcript.
  // Carries what was surfaced and how — see MemoryRecallSchema for why "memory"
  // here is not the SDK's two other memory vocabularies.
  MemoryRecall: "memory_recall",
  // A batch of the turn's files was persisted, and this is the receipt: the ones
  // that landed with their assigned handles, and the ones that did not with the
  // SDK's reason. See FilesPersistedSchema.
  FilesPersisted: "files_persisted",
  // A short natural-language caption for a tool call that has just run, emitted,
  // once per call where the runner environment arms it. Names the calls it,
  // captions by id, because the caption arrives after their own carriers have,
  // closed — see ToolUseSummarySchema.,
  ToolUseSummary: "tool_use_summary",
  // The model's guess at what the user will ask next, produced once per turn,
  // and arriving after that turn's result — which is why it is a kind and not a,
  // field on the row that just closed. See PromptSuggestionSchema.,
  PromptSuggestion: "prompt_suggestion",
  // One tool call was auto-denied without an interactive prompt. Per call, and,
  // strictly narrower than the turn's denial record on `result` — see,
  // PermissionDeniedSchema for the paths it does not cover.,
  PermissionDenied: "permission_denied",
  // The host worker announced a graceful teardown and said why. A live-tail,
  // signal ONLY: the SDK stores these in the durable session stream, so a,
  // resumed session replays historical instances mid-stream — see,
  // WorkerShuttingDownSchema before acting on one.,
  WorkerShuttingDown: "worker_shutting_down",
  // A control request the runner made is still in flight — accepted, or,
  // retrying against the API. Its `requestId` names an exchange internal to the,
  // sandbox and joins to nothing downstream; see ControlRequestProgressSchema.,
  ControlRequestProgress: "control_request_progress",
  // The session's slash-command palette changed mid-run. REPLACE semantics:
  // the payload is the whole list, not a delta — see CommandsChangedSchema for
  // why an empty one is a legitimate replacement and an unreadable one is not.
  CommandsChanged: "commands_changed",
  // The SDK reporting on its own credential state — an exchange under way, the
  // lines it produced, and the error it failed with. Without it an
  // authentication failure inside the sandbox is invisible and the session reads
  // as hung; see AuthStatusSchema.
  AuthStatus: "auth_status",
  // Where the session stands against its rate-limit window, and whether it is
  // drawing on overage. Typed against its own reachability evidence because the
  // ticket names it — see RateLimitSchema, which records the fork and what
  // overturning it costs.
  RateLimit: "rate_limit"
} as const;

/**
 * The kind a runner bundle predating the peer-message naming emits for this same
 * delivery. A sandbox boots from its own snapshot and outlives the deploy that
 * replaced it, so a snapshot baked before that naming goes on emitting this value
 * against a host that has already moved — `parseAgentIpcEvent` reads it as a peer
 * message so the union declares one value and every consumer keeps one switch arm.
 */
export const LEGACY_PEER_MESSAGE_KIND = "teammate_message";

export type AgentIpcKind = (typeof AGENT_IPC_KIND)[keyof typeof AGENT_IPC_KIND];
